import type { AuditTrailGetRequestDto } from './dto/auditTrail-get.request.dto.js';
import type { UpdateFactRequestDto } from '../matter/fact/dto/request-update-fact.dto.js';
import type { SetKeyRequestDto } from '../matter/fact/dto/request-set-key.dto.js';
import type { MatterUpdateRequestDto } from '../matter/dto/matter-update.request.dto.js';
import type { UpdateSubsetRequestDto } from '../matter/subset/dto/update-subset.request.dto.js';
import type { UpdateChronologyRequestDto } from '../matter/subset/chronology/dto/request-update.chronology.dto.js';
import type { UpdateDocChronRequestDto } from '../matter/subset/chronology/doc-chronology/dto/update-doc-chron-title-request.dto.js';
import type { UpdateDocChronDocRequestDto } from '../matter/subset/chronology/doc-chronology/dto/update-doc-chron-doc-request.dto.js';

export enum AuditTarget {
  Matter = 'matter',
  Subset = 'subset',
  User = 'user',
  Run = 'run',
  Document = 'document',
  Fact = 'fact',
  Chronology = 'chronology',
  DocChronology = 'docChronology',
  Comment = 'comment',
  Realtime = 'realtime',
  KimThread = 'kim-thread',
  InconsistencyReport = 'inconsistencyReport',
}

export enum AuditVerb {
  Created = 'created',
  Updated = 'updated',
  Deleted = 'deleted',
  Added = 'added',
  Removed = 'removed',
  Verified = 'verified',
  Archived = 'archived',
}

export enum AuditEventType {
  // Matter
  CreatedMatter = `${AuditVerb.Created}-${AuditTarget.Matter}`,
  UpdatedMatter = `${AuditVerb.Updated}-${AuditTarget.Matter}`,
  DeletedMatter = `${AuditVerb.Deleted}-${AuditTarget.Matter}`,
  AddedUserMatter = `${AuditVerb.Added}-${AuditTarget.User}-${AuditTarget.Matter}`,
  RemovedUserMatter = `${AuditVerb.Removed}-${AuditTarget.User}-${AuditTarget.Matter}`,

  // Subset
  CreatedSubset = `${AuditVerb.Created}-${AuditTarget.Subset}`,
  UpdatedSubset = `${AuditVerb.Updated}-${AuditTarget.Subset}`,
  DeletedSubset = `${AuditVerb.Deleted}-${AuditTarget.Subset}`,

  // Run
  CreatedRun = `${AuditVerb.Created}-${AuditTarget.Run}`,

  // Document
  CreatedDocument = `${AuditVerb.Created}-${AuditTarget.Document}`,
  DeletedDocument = `${AuditVerb.Deleted}-${AuditTarget.Document}`,

  // Fact
  CreatedFact = `${AuditVerb.Created}-${AuditTarget.Fact}`,
  UpdatedFact = `${AuditVerb.Updated}-${AuditTarget.Fact}`,
  ArchivedFact = `${AuditVerb.Archived}-${AuditTarget.Fact}`,

  // Chronology
  CreatedChronology = `${AuditVerb.Created}-${AuditTarget.Chronology}`,
  UpdatedChronology = `${AuditVerb.Updated}-${AuditTarget.Chronology}`,
  DeletedChronology = `${AuditVerb.Deleted}-${AuditTarget.Chronology}`,
  VerifiedFactChronology = `${AuditVerb.Verified}-${AuditTarget.Fact}-${AuditTarget.Chronology}`,

  // DocChronology
  CreatedDocChronology = `${AuditVerb.Created}-${AuditTarget.DocChronology}`,
  UpdatedDocChronology = `${AuditVerb.Updated}-${AuditTarget.DocChronology}`,
  DeletedDocChronology = `${AuditVerb.Deleted}-${AuditTarget.DocChronology}`,
  UpdatedDocChronologyDoc = `${AuditVerb.Updated}-${AuditTarget.DocChronology}-${AuditTarget.Document}`,
  AddedFactToDocChronologyDoc = `${AuditVerb.Added}-${AuditTarget.Fact}-${AuditTarget.DocChronology}-${AuditTarget.Document}`,
  RemovedFactFromDocChronologyDoc = `${AuditVerb.Removed}-${AuditTarget.Fact}-${AuditTarget.DocChronology}-${AuditTarget.Document}`,

  // Added events (verb-source-destination pattern)
  AddedFactToDocument = `${AuditVerb.Added}-${AuditTarget.Fact}-${AuditTarget.Document}`,
  AddedFactToChronology = `${AuditVerb.Added}-${AuditTarget.Fact}-${AuditTarget.Chronology}`,
  AddedFactToDocChronology = `${AuditVerb.Added}-${AuditTarget.Fact}-${AuditTarget.DocChronology}`,
  AddedDocumentToDocChronology = `${AuditVerb.Added}-${AuditTarget.Document}-${AuditTarget.DocChronology}`,
  RemovedDocumentFromDocChronology = `${AuditVerb.Removed}-${AuditTarget.Document}-${AuditTarget.DocChronology}`,
  AddedCommentToFact = `${AuditVerb.Added}-${AuditTarget.Comment}-${AuditTarget.Fact}`,

  // Comment events
  UpdatedComment = `${AuditVerb.Updated}-${AuditTarget.Comment}`,
  DeletedComment = `${AuditVerb.Deleted}-${AuditTarget.Comment}`,

  // Realtime
  CreatedRealtime = `${AuditVerb.Created}-${AuditTarget.Realtime}`,

  // KimThread
  CreatedKimThread = `${AuditVerb.Created}-${AuditTarget.KimThread}`,
  DeletedKimThread = `${AuditVerb.Deleted}-${AuditTarget.KimThread}`,

  // InconsistencyReport
  CreatedInconsistencyReport = `${AuditVerb.Created}-${AuditTarget.InconsistencyReport}`,
  DeletedInconsistencyReport = `${AuditVerb.Deleted}-${AuditTarget.InconsistencyReport}`,
}

// Union of all potential types with auditEventType as the discriminator
// Grouped by similar structure for easier parsing
export type AuditDetail =
  | EntityDetail
  | SubsetEntityDetail
  | CountDetail
  | SubsetCountDetail
  | UpdateFactDetail
  | UpdateMatterDetail
  | UpdateSubsetDetail
  | UpdateChronologyDetail
  | UpdateDocChronologyDetail
  | UpdateDocChronologyDocDetail
  | RelationshipDetail
  | RelationshipMatterDetail
  | ChronologyRelationshipDetail
  | DocChronologyDocRelationshipDetail;

// Entity detail - for create/update/delete of named entities (matter-level)

type BaseDetail = {
  name: string;
};
type SubsetId = {
  subsetId: string;
};
type EntityDetail = {
  auditEventType:
    | AuditEventType.CreatedMatter
    | AuditEventType.DeletedMatter
    | AuditEventType.CreatedSubset
    | AuditEventType.DeletedSubset
    | AuditEventType.DeletedDocument
    | AuditEventType.CreatedRealtime;
} & BaseDetail;

// Entity detail for subset-nested entities (requires subsetId for routing)
type SubsetEntityDetail = {
  auditEventType:
    | AuditEventType.CreatedKimThread
    | AuditEventType.DeletedChronology
    | AuditEventType.DeletedDocChronology
    | AuditEventType.DeletedKimThread
    | AuditEventType.CreatedFact
    | AuditEventType.ArchivedFact
    | AuditEventType.CreatedInconsistencyReport
    | AuditEventType.DeletedInconsistencyReport;
} & BaseDetail &
  SubsetId;

// Count detail - for batch creation operations (matter-level)
type CountDetail = {
  auditEventType: AuditEventType.CreatedRun | AuditEventType.CreatedDocument;
  count: number;
} & BaseDetail;

// Created with X docs
type SubsetCountDetail = {
  auditEventType: AuditEventType.CreatedChronology | AuditEventType.CreatedDocChronology;
  count: number;
} & BaseDetail &
  SubsetId;

// Update detail - for fact field updates (now includes optional subsetId/targetId for deep-linking)

type Update = {
  field: string;
  to: string;
  from: string;
};
type UpdateMatterDetail = {
  auditEventType: AuditEventType.UpdatedMatter;
  field: keyof MatterUpdateRequestDto;
} & Update;

type UpdateSubsetDetail = {
  auditEventType: AuditEventType.UpdatedSubset;
  field: keyof UpdateSubsetRequestDto;
} & Update;

type UpdateFactDetail = {
  auditEventType: AuditEventType.UpdatedFact;
  field: keyof UpdateFactRequestDto | keyof Omit<SetKeyRequestDto, 'factIds'>;
} & Update &
  SubsetId;

type UpdateChronologyDetail = {
  auditEventType: AuditEventType.UpdatedChronology;
  field: keyof UpdateChronologyRequestDto;
} & Update &
  SubsetId;

type UpdateDocChronologyDetail = {
  auditEventType: AuditEventType.UpdatedDocChronology;
  field: keyof UpdateDocChronRequestDto;
} & Update &
  SubsetId;

type UpdateDocChronologyDocDetail = {
  auditEventType: AuditEventType.UpdatedDocChronologyDoc;
  field: keyof UpdateDocChronDocRequestDto;
  docId: string;
  chronologyName: string;
} & Update &
  SubsetId;

// Relationship detail - for adding items to other entities
type Relationship = {
  sourceId: string;
  sourceName: string;
};
type RelationshipMatterDetail = {
  auditEventType: AuditEventType.AddedUserMatter | AuditEventType.RemovedUserMatter;
} & Relationship;
type RelationshipDetail = {
  auditEventType:
    | AuditEventType.AddedFactToDocument
    | AuditEventType.AddedCommentToFact
    | AuditEventType.UpdatedComment
    | AuditEventType.DeletedComment;
} & Relationship &
  SubsetId;

type ChronologyRelationshipDetail = {
  auditEventType: AuditEventType.VerifiedFactChronology | AuditEventType.AddedFactToChronology;
  chronologyName: string;
} & Relationship &
  SubsetId;

// Doc Chronology Doc relationship detail - includes docId and chronologyId for linking
type DocChronologyDocRelationshipDetail = {
  auditEventType:
    | AuditEventType.AddedFactToDocChronologyDoc
    | AuditEventType.RemovedFactFromDocChronologyDoc
    | AuditEventType.AddedDocumentToDocChronology
    | AuditEventType.RemovedDocumentFromDocChronology;
  docId: string;
  chronologyName: string;
} & Relationship &
  SubsetId;

export type AuditTrailFilters = Partial<AuditTrailGetRequestDto>;

export const AUDIT_TRAIL_SORTABLE_COLUMNS = ['eventDate', 'auditEventType', 'user'];

// Auditable fields for update operations - excludes fields handled separately (e.g., userIds, docIds)
export const AUDITABLE_MATTER_FIELDS = [
  'name',
  'description',
  'code',
  'tokenLimit',
  'isDraft',
  'issues',
  'disputeType',
  'userDefinedEntities',
  'additionalContext',
  'timePeriod',
  'disputeStatus',
] as const satisfies readonly (keyof MatterUpdateRequestDto)[];

export const AUDITABLE_SUBSET_FIELDS = [
  'name',
  'legalIssues',
] as const satisfies readonly (keyof UpdateSubsetRequestDto)[];

export const AUDITABLE_FACT_FIELDS = [
  'relevance',
  'significance',
  'description',
] as const satisfies readonly (keyof UpdateFactRequestDto)[];

export const AUDITABLE_CHRONOLOGY_FIELDS = [
  'title',
] as const satisfies readonly (keyof UpdateChronologyRequestDto)[];

export const AUDITABLE_DOC_CHRONOLOGY_FIELDS = [
  'title',
] as const satisfies readonly (keyof UpdateDocChronRequestDto)[];

export const AUDITABLE_DOC_CHRON_DOC_FIELDS = [
  'summary',
  'dateText',
  'title',
] as const satisfies readonly (keyof UpdateDocChronDocRequestDto)[];

export enum AuditTrailDownloadColumn {
  EventDate = 'Event Date',
  User = 'User',
  EventType = 'Event Type',
  MatterName = 'Matter',
  Details = 'Details',
}
